import { createInventory, type Inventory } from "../inventory/state.js";
import {
  ITEM_AMMO_8X57_FMJ,
  ITEM_CARBINE,
  ITEM_CARBINE_MAG_30,
  ITEM_MED_VERBAND,
  ITEM_PACK_WANDER,
  ITEM_VEST_GENDARM,
} from "./db.js";
import { createContainerGrid } from "../inventory/grid.js";
import { mintFactoryMods } from "./mods.js";
import type { ItemInstance } from "./types.js";

/**
 * What every PMC walks into a raid carrying — chosen so the first thing a player
 * does is loot, not shop: the Jagdkarabiner ASSEMBLED (walnut stock, vital
 * barrel, factory 30-round box full of 8×57 Vollmantel — the rounds live on the
 * MAGAZINE), the Gendarmerie vest, an EMPTY Wanderrucksack, and in the pockets
 * a loose 30-round stack, a Verbandpäckchen and a SECOND full magazine. The
 * spare is the lesson: R swaps magazines, so a kit with one box cannot reload;
 * the loose stack is where a player discovers filling one. No rig — the first
 * upgrade worth finding. Server and tests both call this.
 */

/** Rounds in the loose stack of ammunition carried in a pocket. */
export const SPARE_AMMO_ROUNDS = 30;

/**
 * Builds a fresh loadout. `nextIid` is called EXACTLY NINE times, in this
 * order: the weapon, its three FACTORY PARTS in `ATTACH_KINDS` order (stock,
 * magazine, barrel), vest, backpack, spare ammo, bandage, spare magazine —
 * fixed so a seeded generator produces the same loadout. The parts come from
 * `mintFactoryMods`, the same function the shop and the gun cabinet use.
 */
export function createDefaultLoadout(nextIid: () => string): Inventory {
  const inv = createInventory();

  // THE FACTORY CARBINE, assembled by the shared minter and then loaded: the
  // box
  // arrives empty, so filling it is a second step. Mutating the magazine in
  // place
  // is safe because nothing else has held it, and it keeps the minter's key
  // order.
  const weapon: ItemInstance = {
    iid: nextIid(),
    defId: ITEM_CARBINE.id,
    stack: 1,
  };
  const mods = mintFactoryMods(ITEM_CARBINE, nextIid);
  if (mods !== undefined) {
    weapon.mods = mods;
    const mag = mods["mag"];
    if (mag !== undefined) {
      mag.mag = ITEM_CARBINE_MAG_30.magCapacity;
      mag.ammoDefId = ITEM_AMMO_8X57_FMJ.id;
    }
  }
  const vest: ItemInstance = {
    iid: nextIid(),
    defId: ITEM_VEST_GENDARM.id,
    stack: 1,
    durability: ITEM_VEST_GENDARM.maxDurability,
  };
  const backpack: ItemInstance = {
    iid: nextIid(),
    defId: ITEM_PACK_WANDER.id,
    stack: 1,
  };

  inv.equipment.weapon = weapon;
  inv.equipment.vest = vest;
  inv.equipment.backpack = backpack;
  // Equipping a container creates its capacity grid; done directly, since there
  // is no source grid.
  inv.backpack = createContainerGrid(ITEM_PACK_WANDER);

  inv.pockets.items.push({
    item: { iid: nextIid(), defId: ITEM_AMMO_8X57_FMJ.id, stack: SPARE_AMMO_ROUNDS },
    x: 0,
    y: 0,
    rot: 0,
  });
  inv.pockets.items.push({
    item: {
      iid: nextIid(),
      defId: ITEM_MED_VERBAND.id,
      stack: 1,
      durability: ITEM_MED_VERBAND.charges,
    },
    x: 1,
    y: 0,
    rot: 0,
  });
  // The second magazine, full. One reload's worth, and the difference between a
  // gun and a club the moment the first one runs dry.
  inv.pockets.items.push({
    item: {
      iid: nextIid(),
      defId: ITEM_CARBINE_MAG_30.id,
      stack: 1,
      mag: ITEM_CARBINE_MAG_30.magCapacity,
      ammoDefId: ITEM_AMMO_8X57_FMJ.id,
    },
    x: 2,
    y: 0,
    // The 30-round 8x57 box is a tall 1x2 item; lay it across the shallow
    // pocket row so the authored footprint and the starter kit both stay true.
    rot: 1,
  });

  return inv;
}
